import { useState } from "react";
import { IonPage, IonContent } from "@ionic/react";
import { Flame, Trophy } from "lucide-react";
import { useAuth } from "../hooks/useAuthStore";
import { useStore } from "../store/useStore";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { PageHeader } from "../components/ui/PageHeader";
import { Avatar } from "../components/ui/Avatar";
import { SegmentedControl } from "../components/ui/SegmentedControl";
import { EmptyState } from "../components/EmptyState";
import { cn } from "../lib/utils";

// Type definitions
interface ILeaderboardUser {
  uuid: string;
  name: string;
  profile_photo_url: string | null;
  current_streak?: number;
  total_points?: number;
}

type Period = "week" | "month" | "year" | "all";

const PERIODS = [
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
  { value: "all", label: "All Time" },
] as const;

const RANK_STYLES: Record<number, string> = {
  1: "bg-amber-300 text-amber-950",
  2: "bg-slate-200 text-slate-700 dark:bg-slate-500/40 dark:text-slate-100",
  3: "bg-orange-200 text-orange-900 dark:bg-orange-500/30 dark:text-orange-100",
};

// Leaderboard Entry Component
const LeaderboardEntry = ({
  user,
  rank,
  isCurrentUser,
}: {
  user: ILeaderboardUser;
  rank: number;
  isCurrentUser: boolean;
}) => {
  return (
    <li
      className={cn(
        "flex items-center gap-3 rounded-3xl p-3 pr-4 border",
        isCurrentUser
          ? "border-primary/40 bg-accent"
          : "border-border bg-card shadow-soft"
      )}
    >
      <span
        className={cn(
          "inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-extrabold shrink-0",
          RANK_STYLES[rank] ?? "bg-surface-raised text-muted-foreground"
        )}
        aria-label={`Rank ${rank}`}
      >
        {rank}
      </span>
      <Avatar
        name={user.name}
        seed={user.uuid}
        photoUrl={user.profile_photo_url}
        size="lg"
      />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-foreground truncate">
          {user.name}
          {isCurrentUser && (
            <span className="font-medium text-muted-foreground"> (You)</span>
          )}
        </div>
        <div className="inline-flex items-center gap-1 text-muted-foreground text-[13px]">
          <Flame className="w-3.5 h-3.5 text-orange-500" aria-hidden="true" />
          {user.current_streak ?? 0} day streak
        </div>
      </div>
      <div className="text-right">
        <div className="text-2xl font-extrabold tabular-nums text-foreground leading-none">
          {user.total_points ?? 0}
        </div>
        <div className="text-muted-foreground text-xs font-semibold">points</div>
      </div>
    </li>
  );
};

export default function LeaderboardPage() {
  const { user } = useAuth();
  const [activePeriod, setActivePeriod] = useState<Period>("week");

  // Fetch user's households
  const currentHouseholdId = useStore((state) => state.currentHouseholdId);
  const households = useStore((state) => state.households);
  const userProfile = useStore((state) => state.userProfile);


  // Hardcoded leaderboard data for now
  const mockLeaderboardData: ILeaderboardUser[] = [
    {
      uuid: user?.id || "1",
      name: userProfile?.name || "You",
      profile_photo_url: userProfile?.profile_photo_url || null,
      current_streak: 0,
      total_points: 0,
    },
  ];

  const currentUserRank =
    mockLeaderboardData.findIndex((u) => u.uuid === user?.id) + 1 || 1;
  const currentHousehold = households.find(
    (h) => h.uuid === currentHouseholdId
  );

  if (!currentHousehold) {
    return (
      <LoadingSpinner message="Loading leaderboard..." />
    );
  }

  return (
    <IonPage>
      <PageHeader title="Leaderboard" subtitle={currentHousehold.name} />
      <IonContent>
        <div className="max-w-lg mx-auto px-4 pt-2 pb-28">
          {/* Your Rank Summary */}
          {currentUserRank > 0 && (
            <div className="relative overflow-hidden rounded-3xl p-5 mb-6 bg-gradient-to-br from-violet-500 to-sky-600 shadow-glow-purple dark:from-violet-700 dark:to-sky-800">
              <div aria-hidden="true" className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-white/10" />
              <div className="relative flex items-center gap-4">
                <span
                  aria-hidden="true"
                  className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/20"
                >
                  <Trophy className="w-8 h-8 text-amber-200" />
                </span>
                <div>
                  <h2 className="text-white text-xl font-extrabold">
                    You're #{currentUserRank}
                  </h2>
                  <p className="text-white/90 text-sm font-medium">
                    {currentUserRank === 1
                      ? "You're leading the pack!"
                      : currentUserRank <= 3
                      ? "You're on the podium!"
                      : "Keep climbing!"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Time Period Tabs */}
          <SegmentedControl
            label="Time period"
            options={PERIODS}
            value={activePeriod}
            onChange={setActivePeriod}
            className="mb-6"
          />

          {/* Leaderboard List */}
          {mockLeaderboardData.length > 0 ? (
            <section>
              <h3 className="section-title mb-3">
                Rankings {activePeriod === "all" ? "" : `(${activePeriod})`}
              </h3>
              <ol className="space-y-2">
                {mockLeaderboardData.map((userEntry, index) => (
                  <LeaderboardEntry
                    key={userEntry.uuid}
                    user={userEntry}
                    rank={index + 1}
                    isCurrentUser={userEntry.uuid === user?.id}
                  />
                ))}
              </ol>
            </section>
          ) : (
            <EmptyState
              icon={Trophy}
              title="No Rankings Yet"
              description="Complete tasks to start competing!"
            />
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
